import { useState, useRef, useEffect, useId, useCallback, KeyboardEvent } from 'react';
import styles from './MonthYearSelector.module.css';

export interface MonthYearValue {
  /** 1..12 */
  month: number;
  /** שנה מלאה (למשל 2026) */
  year: number;
}

export type MonthYearVariant = 'inline' | 'dropdown' | 'compact';

export interface MonthYearSelectorProps {
  /** הערך הנבחר */
  value: MonthYearValue;
  /** callback בשינוי */
  onChange: (v: MonthYearValue) => void;
  /** תאריך מינימלי (כולל החודש) */
  minDate?: Date;
  /** תאריך מקסימלי (כולל החודש) */
  maxDate?: Date;
  /** inline (2 selects) | dropdown (grid picker) | compact (chevron button) */
  variant?: MonthYearVariant;
  /** הצג badge "נוכחי" אם החודש = החודש הנוכחי */
  showCurrentBadge?: boolean;
  disabled?: boolean;
  /** aria-label על ה-root container */
  ariaLabel?: string;
  id?: string;
  className?: string;
}

/** שמות החודשים בעברית — hardcoded (Date.toLocaleString לא יציב בין דפדפנים) */
const HEBREW_MONTHS: readonly string[] = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
];

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(' ');

const isInRange = (
  month: number,
  year: number,
  minDate?: Date,
  maxDate?: Date
): boolean => {
  const candidate = new Date(year, month - 1, 1).getTime();
  if (minDate) {
    const min = new Date(minDate.getFullYear(), minDate.getMonth(), 1).getTime();
    if (candidate < min) return false;
  }
  if (maxDate) {
    const max = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1).getTime();
    if (candidate > max) return false;
  }
  return true;
};

const isCurrent = (month: number, year: number): boolean => {
  const now = new Date();
  return month === now.getMonth() + 1 && year === now.getFullYear();
};

const buildYearRange = (
  minDate: Date | undefined,
  maxDate: Date | undefined,
  currentYear: number
): number[] => {
  const minY = minDate ? minDate.getFullYear() : currentYear - 5;
  const maxY = maxDate ? maxDate.getFullYear() : currentYear + 5;
  const out: number[] = [];
  for (let y = minY; y <= maxY; y += 1) out.push(y);
  return out;
};

/**
 * בחירת חודש + שנה ב-3 וריאנטים. RTL-aware (ArrowRight=הבא).
 *
 * **משותף ל-6+ מסכים** ב-Profitability. תומך ב-keyboard nav, locale עברי hardcoded,
 * וטווח דינמי לפי `minDate`/`maxDate`.
 *
 * @example
 * const [date, setDate] = useState({ month: 5, year: 2026 });
 * <MonthYearSelector value={date} onChange={setDate} variant="inline" />
 */
export const MonthYearSelector = ({
  value,
  onChange,
  minDate,
  maxDate,
  variant = 'inline',
  showCurrentBadge = false,
  disabled = false,
  ariaLabel,
  id,
  className,
}: MonthYearSelectorProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const reactId = useId();
  const componentId = id || reactId;
  const containerRef = useRef<HTMLDivElement | null>(null);

  const tryCommit = useCallback(
    (month: number, year: number): void => {
      if (disabled) return;
      if (!isInRange(month, year, minDate, maxDate)) return;
      onChange({ month, year });
    },
    [disabled, minDate, maxDate, onChange]
  );

  const navigate = useCallback(
    (delta: number, axis: 'month' | 'year'): void => {
      let { month, year } = value;
      if (axis === 'month') {
        month += delta;
        if (month > 12) {
          month = 1;
          year += 1;
        }
        if (month < 1) {
          month = 12;
          year -= 1;
        }
      } else {
        year += delta;
      }
      tryCommit(month, year);
    },
    [value, tryCommit]
  );

  // ─── Click outside → close ────────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // ─── Keyboard handler (גלובלי ל-root) ─────────────────────────────────
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        navigate(1, 'month');
        break;
      case 'ArrowLeft':
        e.preventDefault();
        navigate(-1, 'month');
        break;
      case 'ArrowUp':
        e.preventDefault();
        navigate(1, 'year');
        break;
      case 'ArrowDown':
        e.preventDefault();
        navigate(-1, 'year');
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          setOpen(false);
        }
        break;
      case 'Enter':
      case ' ':
        if (variant !== 'inline' && !open) {
          e.preventDefault();
          setOpen(true);
        }
        break;
      default:
        break;
    }
  };

  const yearRange = buildYearRange(minDate, maxDate, value.year);
  const labelText = `${HEBREW_MONTHS[value.month - 1]} ${value.year}`;
  const showBadge = showCurrentBadge && isCurrent(value.month, value.year);

  // ─── Variant: inline (2 native selects) ───────────────────────────────
  if (variant === 'inline') {
    const monthSelectId = `${componentId}-month`;
    const yearSelectId = `${componentId}-year`;
    return (
      <div
        ref={containerRef}
        className={cx(styles.root, styles.inline, disabled && styles.disabled, className)}
        role="group"
        aria-label={ariaLabel || 'בחירת חודש ושנה'}
        onKeyDown={handleKeyDown}
        data-testid="month-year-selector"
      >
        <label htmlFor={monthSelectId} className={styles.visuallyHidden}>
          חודש
        </label>
        <select
          id={monthSelectId}
          className={styles.select}
          value={value.month}
          onChange={(e) => tryCommit(Number(e.target.value), value.year)}
          disabled={disabled}
          aria-label="חודש"
          data-testid="month-year-selector-month"
        >
          {HEBREW_MONTHS.map((name, idx) => {
            const m = idx + 1;
            const enabled = isInRange(m, value.year, minDate, maxDate);
            return (
              <option key={m} value={m} disabled={!enabled}>
                {name}
              </option>
            );
          })}
        </select>

        <label htmlFor={yearSelectId} className={styles.visuallyHidden}>
          שנה
        </label>
        <select
          id={yearSelectId}
          className={styles.select}
          value={value.year}
          onChange={(e) => tryCommit(value.month, Number(e.target.value))}
          disabled={disabled}
          aria-label="שנה"
          data-testid="month-year-selector-year"
        >
          {yearRange.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>

        {showBadge && (
          <span className={styles.currentBadge} data-testid="month-year-selector-current">
            נוכחי
          </span>
        )}
      </div>
    );
  }

  // ─── Variant: dropdown / compact (custom picker) ─────────────────────
  const isCompact = variant === 'compact';
  const triggerLabel = isCompact ? labelText : `${labelText}${showBadge ? ' • נוכחי' : ''}`;

  return (
    <div
      ref={containerRef}
      className={cx(
        styles.root,
        isCompact ? styles.compact : styles.dropdown,
        disabled && styles.disabled,
        className
      )}
      role="group"
      aria-label={ariaLabel || 'בחירת חודש ושנה'}
      onKeyDown={handleKeyDown}
      data-testid="month-year-selector"
    >
      <button
        type="button"
        className={styles.trigger}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${componentId}-listbox`}
        data-testid="month-year-selector-trigger"
      >
        <span className={styles.triggerLabel}>{triggerLabel}</span>
        <span className={styles.chevron} aria-hidden="true">
          {open ? '▴' : '▾'}
        </span>
        {showBadge && !isCompact && (
          <span className={styles.currentBadgeInline} data-testid="month-year-selector-current">
            נוכחי
          </span>
        )}
      </button>

      {open && (
        <div
          className={styles.panel}
          id={`${componentId}-listbox`}
          role="dialog"
          aria-label="בחירת חודש ושנה"
          data-testid="month-year-selector-panel"
        >
          {/* year nav */}
          <div className={styles.yearNav}>
            <button
              type="button"
              className={styles.yearArrow}
              onClick={() => navigate(-1, 'year')}
              aria-label="שנה קודמת"
              data-testid="month-year-selector-year-prev"
            >
              ‹
            </button>
            <span className={styles.yearLabel}>{value.year}</span>
            <button
              type="button"
              className={styles.yearArrow}
              onClick={() => navigate(1, 'year')}
              aria-label="שנה הבאה"
              data-testid="month-year-selector-year-next"
            >
              ›
            </button>
          </div>

          <div className={styles.grid} role="listbox" aria-label="חודשים">
            {HEBREW_MONTHS.map((name, idx) => {
              const m = idx + 1;
              const enabled = isInRange(m, value.year, minDate, maxDate);
              const selected = m === value.month;
              const current = isCurrent(m, value.year);
              return (
                <button
                  key={m}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={!enabled}
                  className={cx(
                    styles.monthCell,
                    selected && styles.monthCellSelected,
                    current && styles.monthCellCurrent
                  )}
                  onClick={() => {
                    tryCommit(m, value.year);
                    setOpen(false);
                  }}
                  data-testid={`month-year-selector-month-${m}`}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
